import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import PageLoader from "@/components/loader/PageLoader";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { format, fromZonedTime, toZonedTime } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import MonthToDateSummary from "./MonthToDateSummary";
import SelectedDateSummary from "./SelectedDateSummary";

const initialSummaryData = {
  total_subtotal_sales: 0,
  total_tips: 0,
  total_tax: 0,
  total_paid_orders: 0,
  total_refunded_orders: 0,
  total_service_fee_charge: 0,
  total_rewards: 0,
  total_price: 0,
  total_refunded_amount: 0,
};

const Report: React.FC = () => {
  const [summaryData, setSummaryData] = useState(initialSummaryData);
  const [monthToDateTotals, setMonthToDateTotals] = useState<{
    total_subtotal_sales: number;
    total_tips: number;
    total_tax: number;
    total_paid_orders: number;
    total_refunded_orders: number;
    total_service_fee_charge: number;
    total_rewards: number;
    total_price: number;
    total_refunded_amount: number;
  }>(initialSummaryData);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [axiosInstance, isFetching] = useAxiosInstance();
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [branchTimezone, setBranchTimezone] = useState<string>("UTC");
  const [lastFetchedStartDate, setLastFetchedStartDate] = useState<
    string | null
  >(null);
  const { t } = useTranslation();

  const formatDateToUTC = (date: Date): string => {
    // Format date directly from UTC values to ensure correct timezone
    return `${date.getUTCFullYear()}-${padZero(date.getUTCMonth() + 1)}-${padZero(date.getUTCDate())}T${padZero(date.getUTCHours())}:${padZero(date.getUTCMinutes())}:${padZero(date.getUTCSeconds())}.000Z`;
  };

  const convertLocalToUTC = (date: Date, isStartOfDay: boolean): Date => {
    // Apply start of day (00:00:00) or end of day (23:59:59)
    const localTimeWithHours = isStartOfDay
      ? new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          0,
          0,
          0,
          0,
        )
      : new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23,
          59,
          59,
          999,
        );

    // Calculate UTC timestamp for America/Chicago
    let utcDate;

    if (branchTimezone === "America/Chicago") {
      // Hardcoded conversion for Chicago
      const isDST = isDateInDST(localTimeWithHours);
      const offset = isDST ? 5 : 6; // Chicago is UTC-6, or UTC-5 during DST

      // Get the local timezone date parts
      const year = localTimeWithHours.getFullYear();
      const month = localTimeWithHours.getMonth();
      const day = localTimeWithHours.getDate();
      const hours = localTimeWithHours.getHours();

      // Create UTC date with the offset
      utcDate = new Date(
        Date.UTC(
          year,
          month,
          day,
          hours + offset, // Add offset to convert Chicago local time to UTC
          isStartOfDay ? 0 : 59,
          isStartOfDay ? 0 : 59,
          isStartOfDay ? 0 : 999,
        ),
      );
    } else {
      // For other timezones, use the date-fns-tz library
      const zonedDate = toZonedTime(localTimeWithHours, branchTimezone);
      utcDate = fromZonedTime(zonedDate, "UTC");
    }

    // Debug logging to verify conversion
    console.log("Branch timezone:", branchTimezone);
    console.log(
      "Local date:",
      `${localTimeWithHours.getFullYear()}-${padZero(localTimeWithHours.getMonth() + 1)}-${padZero(localTimeWithHours.getDate())} ${padZero(localTimeWithHours.getHours())}:${padZero(localTimeWithHours.getMinutes())}:${padZero(localTimeWithHours.getSeconds())}`,
    );
    console.log(
      "UTC date:",
      `${utcDate.getUTCFullYear()}-${padZero(utcDate.getUTCMonth() + 1)}-${padZero(utcDate.getUTCDate())} ${padZero(utcDate.getUTCHours())}:${padZero(utcDate.getUTCMinutes())}:${padZero(utcDate.getUTCSeconds())}`,
    );

    // Log the formatted date that will be sent to API
    const formattedDate = formatDateToUTC(utcDate);
    console.log("API date string:", formattedDate);

    return utcDate;
  };

  // Helper function to pad single digit numbers with a leading zero
  const padZero = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`;
  };

  // Helper to determine if a date is in DST for North America
  const isDateInDST = (date: Date): boolean => {
    // DST in the US starts on the second Sunday in March and ends on the first Sunday in November
    const year = date.getFullYear();

    // Create dates for start and end of DST for the given year
    const dstStart = new Date(year, 2, 1); // March 1
    const dstEnd = new Date(year, 10, 1); // November 1

    // Find second Sunday in March
    dstStart.setDate(dstStart.getDate() + ((14 - dstStart.getDay()) % 7));

    // Find first Sunday in November
    dstEnd.setDate(dstEnd.getDate() + ((7 - dstEnd.getDay()) % 7));

    // Adjust times to 2am and 1am respectively
    dstStart.setHours(2, 0, 0, 0);
    dstEnd.setHours(1, 0, 0, 0);

    // Check if the date is between these times
    return date >= dstStart && date < dstEnd;
  };

  const getDateRange = (start: Date, end: Date) => {
    // For start date, get 00:00:00 in branch timezone, converted to UTC
    const startUTC = convertLocalToUTC(start, true);
    // For end date, get 23:59:59.999 in branch timezone, converted to UTC
    const endUTC = convertLocalToUTC(end, false);

    return {
      startDate: formatDateToUTC(startUTC),
      endDate: formatDateToUTC(endUTC),
    };
  };

  const getMonthToDateRange = () => {
    const now = new Date();
    // Get current date/time in branch timezone
    const zonedNow = toZonedTime(now, branchTimezone);
    // Get first day of month in branch timezone
    const firstDayOfMonth = startOfMonth(zonedNow);

    // Convert to UTC properly preserving the branch timezone boundaries
    const startUTC = convertLocalToUTC(firstDayOfMonth, true);
    const endUTC = convertLocalToUTC(zonedNow, false);

    return {
      startDate: formatDateToUTC(startUTC),
      endDate: formatDateToUTC(endUTC),
    };
  };

  useEffect(() => {
    const storedData = localStorage.getItem("user_data");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.selectedBranch && parsedData.selectedBranch.id) {
          setSelectedBranch(parsedData.selectedBranch.id);

          // Set the branch timezone from localStorage
          if (parsedData.selectedBranch.timezone) {
            setBranchTimezone(parsedData.selectedBranch.timezone);
          } else {
            console.warn("No timezone found for selected branch, using UTC");
            setBranchTimezone("UTC");
          }
        } else {
          console.warn("No valid selected branch found in localStorage");
        }
      } catch (error) {
        toast.error("Failed to parse user data from localStorage");
        console.error("Error parsing user data from localStorage:", error);
      }
    } else {
      console.warn("No user data found in localStorage");
    }
  }, []);

  const fetchSummaryData = async () => {
    try {
      setTotalsLoading(true);
      const { startDate: start, endDate: end } = getDateRange(
        startDate || new Date(),
        endDate || new Date(),
      );

      const url = `/api/summary/${selectedBranch}/?start_date=${start}&end_date=${end}`;
      const response = await axiosInstance.get(url);
      if (!response.status.toString().startsWith("2")) {
        toast.error("Failed to fetch summary data");
        throw new Error("Failed to fetch summary data");
      }
      setSummaryData({
        total_subtotal_sales: Number(response.data.total_subtotal_sales) || 0,
        total_tips: Number(response.data.total_tips) || 0,
        total_tax: Number(response.data.total_tax) || 0,
        total_paid_orders: Number(response.data.total_paid_orders) || 0,
        total_refunded_orders: Number(response.data.total_refunded_orders) || 0,
        total_service_fee_charge:
          Number(response.data.total_service_fee_charge) || 0,
        total_rewards: Number(response.data.total_rewards) || 0,
        total_price: Number(response.data.total_price) || 0,
        total_refunded_amount: Number(response.data.total_refunded_amount) || 0,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setTotalsLoading(false);
    }
  };

  const fetchMonthToDateTotals = async () => {
    try {
      const { startDate, endDate } = getMonthToDateRange();
      const url = `/api/summary/${selectedBranch}/?start_date=${startDate}&end_date=${endDate}`;

      const response = await axiosInstance.get(url);
      if (!response.status.toString().startsWith("2")) {
        toast.error("Failed to fetch month-to-date totals");
        throw new Error("Failed to fetch month-to-date totals");
      }
      setMonthToDateTotals({
        total_subtotal_sales: Number(response.data.total_subtotal_sales) || 0,
        total_tips: Number(response.data.total_tips) || 0,
        total_tax: Number(response.data.total_tax) || 0,
        total_paid_orders: Number(response.data.total_paid_orders) || 0,
        total_refunded_orders: Number(response.data.total_refunded_orders) || 0,
        total_service_fee_charge:
          Number(response.data.total_service_fee_charge) || 0,
        total_rewards: Number(response.data.total_rewards) || 0,
        total_price: Number(response.data.total_price) || 0,
        total_refunded_amount: Number(response.data.total_refunded_amount) || 0,
      });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedBranch) {
        const { startDate } = getMonthToDateRange();
        if (startDate !== lastFetchedStartDate) {
          await fetchMonthToDateTotals();
          setLastFetchedStartDate(startDate);
        }
        await fetchSummaryData();
      }
      setLoading(false);
    };

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [selectedBranch, startDate, endDate, branchTimezone]);

  const handleDirectPrint = async () => {
    try {
      const { startDate: start, endDate: end } = getDateRange(
        startDate || new Date(),
        endDate || new Date(),
      );
      const requestData = {
        branch_id: selectedBranch,
        start_date: start,
        end_date: end,
      };

      const response = await axiosInstance.post(
        "/print/print-daily-report/",
        requestData,
      );

      if (response.status === 200) {
        console.log("Print job sent successfully");
      } else {
        throw new Error("Failed to send print job");
      }
    } catch (error) {
      console.error("Error sending print job:", error);
      toast.error("Failed to print report. Please try again.");
    }
  };

  return (
    <BranchRequiredWrapper>
      <PageLoader isFetching={isFetching}>
        <div className="min-h-screen p-4">
          <MonthToDateSummary
            monthToDateTotals={monthToDateTotals}
            loading={loading}
          />

          <div className="my-6 flex justify-center">
            <DateFilterButton
              onStartDateChange={(date) => setStartDate(date)}
              onEndDateChange={(date) => setEndDate(date)}
              startDate={startDate}
              endDate={endDate}
              labelText="Filter by date"
            />
            <button
              onClick={handleDirectPrint}
              className="rounded bg-red-500 px-4 py-2 font-bold text-white hover:bg-red-600"
            >
              {t("back.management.report.print")}
            </button>
          </div>

          <SelectedDateSummary
            summaryData={summaryData}
            loading={totalsLoading}
          />
        </div>
      </PageLoader>
    </BranchRequiredWrapper>
  );
};

export default Report;
